import type { PortalProfile, PortalSellerGroupContext } from "./portal.types";

export const ACTIVE_SELLER_GROUP_HEADER = "X-Active-Seller-Group";
export const ACTIVE_SELLER_GROUP_COOKIE = "abbiamo.activeSellerGroupId";

// Supported local-runtime replacement for product subdomains. Production
// handoff uses the product-scoped active-seller-group cookie.
const PRODUCT_PARAM = "product";
const SELLER_GROUP_PARAM = "sg";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface LocalPortalContext {
  product: PortalProfile;
  sellerGroupId: string;
}

let activeSellerGroupId: string | null = null;

// Scoped cookie name per product — prevents LOG/GO/PAY contexts from
// overwriting each other when multiple product tabs are open simultaneously.
const getProductCookieName = (profile: PortalProfile): string =>
  `${ACTIVE_SELLER_GROUP_COOKIE}.${profile}`;

// Infers the active product from the subdomain so dashboard pages can read
// their own cookie without an explicit profile argument.
const detectCurrentProduct = (): PortalProfile | null => {
  if (typeof window === "undefined") return null;
  const { hostname } = window.location;
  if (hostname.startsWith("log.")) return "LOG";
  if (hostname.startsWith("go.")) return "GO";
  if (hostname.startsWith("pay.")) return "PAY";
  return null;
};

export const clearActiveSellerGroupId = (): void => {
  activeSellerGroupId = null;

  if (typeof document === "undefined") return;

  const namesToClear: string[] = [
    ACTIVE_SELLER_GROUP_COOKIE,
    getProductCookieName("LOG"),
    getProductCookieName("GO"),
    getProductCookieName("PAY"),
  ];

  for (const name of namesToClear) {
    const attributes = [`${name}=`, "Path=/", "Max-Age=0", "SameSite=Lax"];
    if (canUseSharedCookieDomain()) {
      attributes.push(`Domain=${getSharedCookieDomain()}`, "Secure");
    }
    document.cookie = attributes.join("; ");
  }
};

const isPortalProfile = (value: string | null): value is PortalProfile =>
  value === "LOG" || value === "GO" || value === "PAY";

export const getLocalPortalContextFromSearch = (
  search: string,
): LocalPortalContext | null => {
  const params = new URLSearchParams(search);
  const product = params.get(PRODUCT_PARAM);
  const sellerGroupId = params.get(SELLER_GROUP_PARAM);

  if (!isPortalProfile(product) || !sellerGroupId) {
    return null;
  }

  return { product, sellerGroupId };
};

export const getLocalPortalContext = (): LocalPortalContext | null => {
  if (typeof window === "undefined") return null;
  return getLocalPortalContextFromSearch(window.location.search);
};

export const getActiveSellerGroupIdFromLocation = (): string | null =>
  getLocalPortalContext()?.sellerGroupId ?? null;

const canUseSharedCookieDomain = () =>
  typeof window !== "undefined" &&
  (process.env.NX_COOKIE_DOMAIN
    ? window.location.hostname.endsWith(
        process.env.NX_COOKIE_DOMAIN.replace(/^\./, ""),
      )
    : window.location.hostname.endsWith(".abbiamolog.com"));

const getSharedCookieDomain = () =>
  process.env.NX_COOKIE_DOMAIN || ".abbiamolog.com";

const getCookieValue = (name: string): string | null => {
  if (typeof document === "undefined") return null;

  const cookie = document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${name}=`));

  if (!cookie) return null;

  return decodeURIComponent(cookie.slice(name.length + 1));
};

const writeCookie = (name: string, value: string): void => {
  const attributes = [
    `${name}=${encodeURIComponent(value)}`,
    "Path=/",
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];

  if (canUseSharedCookieDomain()) {
    attributes.push(`Domain=${getSharedCookieDomain()}`, "Secure");
  }

  document.cookie = attributes.join("; ");
};

// profile is required when called from the portal handoff (portal-selection)
// so the cookie is product-scoped. Without profile (local dev / legacy paths)
// the generic cookie is written as a fallback.
export const setActiveSellerGroupId = (
  sellerGroupId: string,
  profile?: PortalProfile,
): void => {
  activeSellerGroupId = sellerGroupId;

  if (typeof document === "undefined") return;

  const cookieName = profile
    ? getProductCookieName(profile)
    : ACTIVE_SELLER_GROUP_COOKIE;

  writeCookie(cookieName, sellerGroupId);
};

export const getActiveSellerGroupId = (): string | null => {
  // Layer 1: in-memory (fastest, isolated per tab JS context)
  if (activeSellerGroupId) return activeSellerGroupId;

  // Layer 2: product-scoped cookie (isolated per product, survives hard refresh)
  const product = detectCurrentProduct();
  if (product) {
    const productValue = getCookieValue(getProductCookieName(product));
    if (productValue) {
      activeSellerGroupId = productValue;
      return productValue;
    }
  }

  // Layer 3: generic cookie (backward compat for sessions active at deploy time)
  const genericValue = getCookieValue(ACTIVE_SELLER_GROUP_COOKIE);
  if (genericValue) {
    activeSellerGroupId = genericValue;
    return genericValue;
  }

  // Layer 4: query-string (local dev only)
  return getActiveSellerGroupIdFromLocation();
};

export const getValidActiveSellerGroupId = (): string | null => {
  const sellerGroupId = getActiveSellerGroupId();

  if (!sellerGroupId || !UUID_REGEX.test(sellerGroupId)) {
    return null;
  }

  return sellerGroupId;
};

export const buildLocalPortalDashboardUrl = (
  context: PortalSellerGroupContext,
): string => {
  const params = new URLSearchParams({
    [PRODUCT_PARAM]: context.profile,
    [SELLER_GROUP_PARAM]: context.id,
  });

  return `/?${params.toString()}`;
};
